import {Controller} from "@hotwired/stimulus"
import L from "leaflet"

export default class extends Controller {
    static values = {
        markerIcon: String,
        markerIcon2x: String,
        markerParking: String,
        markerParkingIcon2x: String,
        markerShadow: String
    }

    declare markerIconValue: string
    declare markerParkingValue: string
    declare markerIcon2xValue: string
    declare markerParking2xValue: string
    declare markerShadowValue: string

    private map: L.Map | null = null

    connect(): void {
        const el = this.element as HTMLElement

        const iconDefault = L.icon({
            iconUrl: this.markerIconValue,
            iconRetinaUrl: this.markerIcon2xValue,
            shadowUrl: this.markerShadowValue,
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
        })

        L.Marker.prototype.options.icon = iconDefault
        const startWhenReady = () => {
            if (el.offsetHeight > 100) { // 100px als Schwellenwert für aktives CSS
                this.initializeMap(el)
            } else {
                requestAnimationFrame(startWhenReady)
            }
        }
        startWhenReady()
    }

    private initializeMap(el: HTMLElement): void {
        if (this.map) return
        const parkIcon = L.icon({
            iconUrl: this.markerParkingValue,
            iconRetinaUrl: this.markerParking2xValue,
            shadowUrl: this.markerShadowValue,
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
        })
        const {rockLat, rockLng, parkingLat, parkingLng, rockName} = el.dataset
        if (!((rockLat && rockLng) || (parkingLat && parkingLng))) {
            return
        }
        let rCoord: L.LatLngTuple = [0, 0]
        if (rockLat && rockLng) {
            rCoord = [parseFloat(rockLat), parseFloat(rockLng)];
        } else if (parkingLat && parkingLng) {
            rCoord = [parseFloat(parkingLat), parseFloat(parkingLng)];
        } else {
            return
        }

        // 3. Map Erstellen (Zentrum direkt setzen)
        this.map = L.map(el, {
            center: rCoord,
            zoom: 14,
            zoomControl: true,
            fadeAnimation: false
        })

        // 4. Tiles über deinen Rails-Proxy
        L.tileLayer('/map_tiles/{z}/{x}/{y}.png', {
            maxZoom: 19,
            minZoom: 2, // Blockiert 0/0/0 und 1/1/0 Anfragen
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> Mitwirkende'
        }).addTo(this.map)

        // 5. Marker für den Fels
        if (rockLat && rockLng) {
            const rockMarker = L.marker(rCoord).addTo(this.map)
            rockMarker.bindTooltip(rockName || '', {
                permanent: true,
                direction: 'top',
                offset: [0, -40],
                className: 'map-label'
            });
        }

        // 6. Marker für den Parkplatz (falls vorhanden)
        if (parkingLat && parkingLng) {
            const pCoord: L.LatLngTuple = [parseFloat(parkingLat), parseFloat(parkingLng)]
            const parkMarker = L.marker(pCoord, {icon: parkIcon}).addTo(this.map)
            parkMarker.bindTooltip("Parkplatz", {
                permanent: true,
                direction: 'top',
                offset: [0, -40],
                className: 'map-label'
            });

            // Zoom so anpassen, dass beide Punkte sichtbar sind
            const bounds = L.latLngBounds([rCoord, pCoord])
            this.map.fitBounds(bounds.pad(0.3))
        }

        // Letzter Render-Check
        setTimeout(() => {
            this.map?.invalidateSize()
        }, 200)
    }

    disconnect(): void {
        if (this.map) {
            this.map.remove()
            this.map = null
        }
    }
}
